import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaPaperPlane } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { sendNewsletterSubscriptionEmail, NewsletterSubscriptionData } from '../../../services/newsletterService';
import './Newsletter.css';

interface NewsletterProps {
  variant?: 'light' | 'dark' | 'primary';
}

const Newsletter: React.FC<NewsletterProps> = ({ variant = 'light' }) => {
  const [email, setEmail] = useState<string>('');
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim() && validateEmail(email)) {
      setIsLoading(true);
      const subscriptionData: NewsletterSubscriptionData = { email };
      const isSuccessful = await sendNewsletterSubscriptionEmail(subscriptionData);
      if (isSuccessful) {
        setIsLoading(false);
        setIsSubmitted(true);
        setEmail('');
        setTimeout(() => setIsSubmitted(false), 5000);
      } else {
        setIsLoading(false);
        console.error('Newsletter subscription failed');
      }
    }
  };
  
  const validateEmail = (email: string): boolean => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  return (
    <section className={`newsletter-section ${variant}`}>
      <div className="container">
        <div className="newsletter-container">
          <motion.div
            className="newsletter-content"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="newsletter-title">{t('footer.newsletter.title')}</h2>
            <h3 className="newsletter-subtitle">{t('footer.newsletter.subtitle')}</h3>
            <p className="newsletter-description-n">
              {t('footer.newsletter.description')}
            </p>
          </motion.div>
          
          <motion.div
            className="newsletter-form-container"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            {isSubmitted ? (
              <div className="success-message">
                <div className="success-icon">✓</div>
                <p className="success-text">{t('footer.newsletter.success')}</p>
              </div>
            ) : (
              <form className="newsletter-form" onSubmit={handleSubmit}>
                <input
                  className="newsletter-input-n"
                  type="email"
                  placeholder={t('footer.newsletter.placeholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <button className="newsletter-button-normal" type="submit" disabled={isLoading}>
                  {isLoading ? (
                    <div className="button-loader" />
                  ) : (
                    <>
                      <span className="button-text">{t('footer.newsletter.button')}</span>
                      <span className="button-icon">
                        <FaPaperPlane />
                      </span>
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Newsletter;
